// Components==============
import { useRedux } from 'hooks/useRedux';
import { useRootModal } from 'hooks/useRootModal';
import React, { createContext, useState } from 'react';
import Modal from '../Modal/Modal';
import Content from './Content/Content';
import './DreamModal.module.scss';
import Edit from './Edit/Edit';
import Top from './Top/Top';
// =========================

export type DreamOriginTypes =
  | 'add-dream'
  | 'template'
  | 'person'
  | 'view'
  | 'skill';

export type DreamEditTypes = {
  image: string;
  file?: File;
  path?: string;
  name: string;
  description: string;
  activities: {
    id: number;
    type: ProcessType;
    amount: number;
  }[];
  skills: {
    id: number;
    amount: number;
  }[];
};

type DreamModalContextType = {
  dream?: DreamEntity;
  originState: DreamOriginTypes;
  setOrigin: React.Dispatch<React.SetStateAction<DreamOriginTypes>>;
  originSkill?: SkillEntity;
  activities?: RequirementEntity[];
  skills?: RequirementEntity[];
  people?: (PeopleEntity | undefined)[];
  editing: boolean;
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose: () => void;
  editState: DreamEditTypes;
  setEditState: React.Dispatch<React.SetStateAction<DreamEditTypes>>;
  defaultState: DreamEditTypes;
};

export const DreamModalContext = createContext({} as DreamModalContextType);

export default function DreamModal({
  setModalIsOpen,
  dream,
  origin,
  originSkill,
  editOnOpen,
}: {
  setModalIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  dream?: DreamEntity;
  origin: DreamOriginTypes;
  originSkill?: SkillEntity;
  editOnOpen?: boolean;
}) {
  const { closeModal } = useRootModal();
  const [editing, setEditing] = useState(
    origin === 'add-dream' || !!editOnOpen
  );

  const activities = dream?.requirements.filter(
    (r) => r.requirement_type !== 'App\\Skill'
  );
  const skills = dream?.requirements.filter(
    (r) => r.requirement_type === 'App\\Skill'
  );

  const people = useRedux((state) =>
    dream?.people?.length
      ? dream.people.map(({ id }) => state.people?.entities.byId[id])
      : undefined
  );

  const defaultState = {
    image: dream?.image || '',
    file: undefined,
    path: dream?.image || undefined,
    name: dream?.name || '',
    description: dream?.description || '',
    activities:
      dream?.requirements
        .filter((r) => r.requirement_type !== 'App\\Skill')
        .map((r) => ({
          amount: r.amount,
          type: r.requirement_type,
          id: r.requirement_id,
        })) || [],
    skills:
      dream?.requirements
        .filter((r) => r.requirement_type === 'App\\Skill')
        .map((r) => ({
          amount: r.amount,
          id: r.requirement_id,
        })) || [],
  };

  const [originState, setOrigin] = useState(origin);

  const [editState, setEditState] = useState<DreamEditTypes>(defaultState);

  const handleClose = () =>
    setModalIsOpen ? setModalIsOpen(false) : closeModal();

  function handleSubmitImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]!;

    const imageReader = new FileReader();

    imageReader.addEventListener('load', () => {
      if (typeof imageReader.result === 'string') {
        setEditState((prev) => ({
          ...prev,
          file,
          path: file.name,
          image: imageReader.result as string,
        }));
      }
    });

    if (file) imageReader.readAsDataURL(file);
  }

  return (
    <DreamModalContext.Provider
      value={{
        dream,
        originState,
        setOrigin,
        originSkill,
        activities,
        skills,
        people,
        setEditing,
        handleClose,
        editing,
        editState,
        setEditState,
        defaultState,
      }}
    >
      <Modal
        setModalIsOpen={setModalIsOpen}
        image={{ image: editState.image, path: editState.path }}
        handleSubmitImage={editing ? handleSubmitImage : undefined}
        noCross
      >
        <Top />
        {editing ? <Edit /> : <Content />}
      </Modal>
    </DreamModalContext.Provider>
  );
}
